import * as Sentry from "@sentry/react";
import { reportToSentry } from "utils/sentry";

// Pemulihan saat chunk hasil build (/assets/*.js|css) gagal di-load.
//
// Kasus utama: saat file chunk tidak ada di server (tab lama setelah deploy /
// momen deploy), Cloudflare membalas index.html (fallback SPA) dengan status 200
// dan header /assets/* `immutable` 1 tahun (public/_headers). Browser menyimpan
// HTML itu sebagai isi file JS, sehingga reload biasa maupun hard refresh tetap
// memakai salinan rusak. Service worker (workbox precache) bisa ikut tercemar.
//
// Langkah pemulihan:
// 1. recoverFromChunkError: diagnosa → lapor Sentry (tag `chunk_verdict`) →
//    unregister SW + hapus Cache Storage → tandai "perlu repair" → reload.
// 2. repairPendingChunks (dipanggil main.jsx sebelum registerSW & render, saat
//    halaman sudah tidak dikontrol SW): cek semua aset di HTTP cache, ambil ulang
//    dari server hanya yang isinya salah. Harus sebelum registerSW karena workbox
//    mem-precache aset ber-hash lewat HTTP cache, jadi bisa ikut tercemar.
// Dibatasi jumlah percobaan per tab supaya tidak reload terus-menerus.

const ATTEMPTS_KEY = "chunk_recovery:attempts"; // per-tab (sessionStorage)
const PENDING_KEY = "chunk_recovery:pending"; // URL aset yang dicurigai rusak
const MAX_ATTEMPTS = 2;
// Cukup panjang supaya siklus reload di jaringan lambat tetap terhitung
const ATTEMPT_WINDOW_MS = 5 * 60 * 1000;
const TIMEOUT_MS = 4000;
const REPAIR_TIMEOUT_MS = 10000;
const REPAIR_BATCH_SIZE = 12;

let recoveryPromise = null;

const readAttempts = () => {
  try {
    const list = JSON.parse(sessionStorage.getItem(ATTEMPTS_KEY) || "[]");
    const now = Date.now();
    return Array.isArray(list)
      ? list.filter((time) => now - time < ATTEMPT_WINDOW_MS)
      : [];
  } catch (_) {
    return [];
  }
};

const writeAttempts = (list) => {
  try {
    sessionStorage.setItem(ATTEMPTS_KEY, JSON.stringify(list));
  } catch (_) {
    /* noop */
  }
};

export const resetChunkRecoveryAttempts = () => {
  try {
    sessionStorage.removeItem(ATTEMPTS_KEY);
  } catch (_) {
    /* noop */
  }
};

const withTimeout = (promise, fallback, ms = TIMEOUT_MS) =>
  Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(fallback), ms)),
  ]);

const ASSET_PATTERN = /\/assets\/[^\s"'?#]+?\.(?:js|css)$/;

const toAssetUrl = (value) => {
  try {
    const url = new URL(value, window.location.origin);
    return url.origin === window.location.origin &&
      ASSET_PATTERN.test(url.pathname)
      ? `${url.origin}${url.pathname}`
      : null;
  } catch (_) {
    return null;
  }
};

const expectedTypeOf = (url) => (url.endsWith(".css") ? "css" : "javascript");

// Ambil URL chunk dari pesan error, mis.
// "Failed to fetch dynamically imported module: https://app.crm.id/assets/index-xxx.js"
// "Unable to preload CSS for /assets/index-xxx.css"
// Safari ("Importing a module script failed.") tidak menyertakan URL → null.
export const getChunkUrl = (error) => {
  const msg = String(error?.message || error?.reason || "");
  const match = msg.match(
    /(?:https?:\/\/[^\s"']+)?\/assets\/[^\s"']+?\.(?:js|css)/,
  );
  return match ? toAssetUrl(match[0]) : null;
};

const probe = async (url, cache) => {
  try {
    const res = await fetch(url, { cache });
    res.body?.cancel?.().catch(() => {});
    return { status: res.status, type: res.headers.get("content-type") || "" };
  } catch (e) {
    return { error: String(e?.message || e) };
  }
};

const probeCacheStorage = async (url) => {
  if (!window.caches) return null;
  try {
    const res = await caches.match(url, { ignoreSearch: true });
    return res
      ? { status: res.status, type: res.headers.get("content-type") || "" }
      : null;
  } catch (e) {
    return { error: String(e?.message || e) };
  }
};

// Bandingkan isi file versi cache vs langsung dari server untuk tahu
// lapisan mana yang rusak.
const diagnose = async (url) => {
  const info = {
    url,
    online: navigator.onLine,
    sw_controlled: Boolean(navigator.serviceWorker?.controller),
    user_agent: navigator.userAgent,
  };
  if (!url) return { ...info, verdict: "unknown_url" };

  const expectedType = expectedTypeOf(url);
  const isValid = (result) => Boolean(result?.type?.includes(expectedType));
  // Query unik supaya tidak cocok dengan precache SW & HTTP cache
  const networkUrl = `${url}${url.includes("?") ? "&" : "?"}chunk_probe=${Date.now()}`;
  const timeout = { error: "timeout" };

  const [cached, network, cacheStorage] = await Promise.all([
    withTimeout(probe(url, "force-cache"), timeout),
    withTimeout(probe(networkUrl, "no-store"), timeout),
    withTimeout(probeCacheStorage(url), timeout),
  ]);

  let verdict = "transient";
  if (!isValid(network)) {
    verdict = network?.error ? "network_error" : "missing_on_server";
  } else if (cacheStorage?.type && !isValid(cacheStorage)) {
    verdict = "service_worker_cache";
  } else if (!isValid(cached)) {
    verdict = "http_cache";
  }

  return { ...info, cached, network, cache_storage: cacheStorage, verdict };
};

const resetServiceWorker = async () => {
  try {
    const regs = (await navigator.serviceWorker?.getRegistrations?.()) || [];
    await Promise.allSettled(regs.map((reg) => reg.unregister()));
  } catch (_) {
    /* noop */
  }
  try {
    const keys = (await window.caches?.keys?.()) || [];
    await Promise.allSettled(keys.map((key) => caches.delete(key)));
  } catch (_) {
    /* noop */
  }
};

// Aset yang dipakai halaman ini (termasuk dependency chunk yang gagal; pesan
// error hanya menyebut chunk induknya).
const collectSuspectUrls = (failedUrl) => {
  const urls = new Set(failedUrl ? [failedUrl] : []);
  try {
    performance.getEntriesByType("resource").forEach((entry) => {
      const url = toAssetUrl(entry.name);
      if (url) urls.add(url);
    });
  } catch (_) {
    /* noop */
  }
  return [...urls];
};

// Semua aset build saat ini, dari manifest precache di sw.js.
const listBuildAssetUrls = async () => {
  try {
    const res = await fetch("/sw.js", { cache: "no-store" });
    const text = await res.text();
    return (text.match(/assets\/[^"'\s]+?\.(?:js|css)/g) || [])
      .map((path) => toAssetUrl(`/${path}`))
      .filter(Boolean);
  } catch (_) {
    return [];
  }
};

// Isi HTTP cache untuk url tsb tanpa menyentuh network.
// null = tidak ada di cache (atau browser tidak mendukung only-if-cached).
const readCachedType = async (url) => {
  try {
    const res = await fetch(url, { cache: "only-if-cached", mode: "same-origin" });
    res.body?.cancel?.().catch(() => {});
    return res.headers.get("content-type") || "";
  } catch (_) {
    return null;
  }
};

// Ambil ulang dari server dan simpan ke HTTP cache, menimpa salinan rusak.
const refetchAsset = async (url) => {
  try {
    const res = await fetch(url, { cache: "reload" });
    await res.blob();
    return true;
  } catch (_) {
    return false;
  }
};

const repairAssets = async (suspectUrls) => {
  const suspects = new Set(suspectUrls);
  const urls = [...new Set([...suspectUrls, ...(await listBuildAssetUrls())])];
  const repaired = [];
  let refetched = 0;

  for (let i = 0; i < urls.length; i += REPAIR_BATCH_SIZE) {
    const batch = urls.slice(i, i + REPAIR_BATCH_SIZE);
    await Promise.all(
      batch.map(async (url) => {
        const type = await readCachedType(url);
        if (type !== null && !type.includes(expectedTypeOf(url))) {
          if (await refetchAsset(url)) repaired.push(url);
        } else if (type === null && suspects.has(url)) {
          // Dicurigai tapi tidak bisa dicek → ambil ulang saja
          if (await refetchAsset(url)) refetched += 1;
        }
      }),
    );
  }

  return { checked: urls.length, repaired, refetched };
};

export const hasPendingChunkRepair = () => {
  try {
    return sessionStorage.getItem(PENDING_KEY) !== null;
  } catch (_) {
    return false;
  }
};

// Dipanggil saat boot setelah reload pemulihan, sebelum registerSW & render.
export const repairPendingChunks = async () => {
  let suspects = [];
  try {
    suspects = JSON.parse(sessionStorage.getItem(PENDING_KEY) || "[]");
    sessionStorage.removeItem(PENDING_KEY);
  } catch (_) {
    /* noop */
  }

  const result = await withTimeout(
    repairAssets(Array.isArray(suspects) ? suspects : []).catch(() => null),
    null,
    REPAIR_TIMEOUT_MS,
  );

  reportToSentry({
    message: "[chunk-recovery] repaired",
    level: "info",
    tags: { chunk_repair: result ? "done" : "timeout" },
    context: {
      name: "chunk_recovery",
      context: {
        checked: result?.checked,
        repaired_count: result?.repaired.length,
        refetched_uncached: result?.refetched,
        repaired: result?.repaired.slice(0, 50),
      },
    },
  });
};

const flushSentry = async () => {
  try {
    await Sentry.flush(2000);
  } catch (_) {
    /* noop */
  }
};

const runRecovery = async (error, source) => {
  if (!navigator.onLine) return false;

  const url = getChunkUrl(error);
  const attempts = readAttempts();
  const giveUp = attempts.length >= MAX_ATTEMPTS;
  const diagnosis = await diagnose(url).catch(() => ({
    url,
    verdict: "diagnose_failed",
  }));

  reportToSentry({
    message: giveUp
      ? "[chunk-recovery] gave up"
      : "[chunk-recovery] recovering",
    level: giveUp ? "error" : "warning",
    tags: { chunk_verdict: diagnosis.verdict, chunk_source: source },
    context: {
      name: "chunk_recovery",
      context: {
        ...diagnosis,
        source,
        attempt: attempts.length + 1,
        error_message: String(error?.message || error),
      },
    },
  });

  if (giveUp) {
    await flushSentry();
    return false;
  }

  writeAttempts([...attempts, Date.now()]);
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(collectSuspectUrls(url)));
  } catch (_) {
    /* noop */
  }
  await resetServiceWorker();
  await flushSentry();

  window.location.reload();
  return true;
};

// Resolve true jika halaman sedang di-reload, false jika menyerah (batas
// percobaan habis / offline) → pemanggil menampilkan tombol muat ulang manual.
// Panggilan bersamaan (event vite + error boundary) berbagi proses yang sama.
export const recoverFromChunkError = (error, source = "unknown") => {
  if (!recoveryPromise) {
    recoveryPromise = runRecovery(error, source)
      .catch(() => false)
      .then((reloading) => {
        if (!reloading) recoveryPromise = null;
        return reloading;
      });
  }
  return recoveryPromise;
};
